// ADR 6.9.26j §1b — the identity join the events are worthless without.
//
// Android does not merely `capture` on registration and login: both call
// `AnalyticsIdentity.signedIn()` (`AnalyticsServiceAuth.kt:27,40`), which is
// 13.8.26a D1's whole subject. Without this module the web would emit
// correctly-named events onto an anonymous distinct id that never becomes a
// person, and `retention` / `docs-readership` would under-count the web
// silently and unbackfillably.
//
// **The seam is here for the reason 13.8.26a already wrote down.**
// `AnalyticsIdentitySink`'s doc comment (`AnalyticsIdentity.kt:13-29`): "without
// this the *order* of `alias` and `identify` … could only be verified by
// reading the code. The seam exists to make acceptance criteria 1–3 assertions
// rather than review obligations." The problem arrives harder on the web,
// because 6.9.26c holds the posthog instance privately behind a dynamic
// `import()` and exports `capture()` and nothing else — so an `identity.ts`
// written directly against posthog-js could neither reach the instance nor be
// observed.

import { attached } from './capture'

/**
 * The two halves of a user-property write (ADR 6.9.26m §1b).
 *
 * `set` is mutable and `setOnce` is frozen at its first value, which is the
 * whole distinction: `review_count` is a running total and must be re-writable,
 * `first_review_session_at` is an activation record and must not be.
 */
export interface UserProperties {
  set?: Record<string, unknown>
  setOnce?: Record<string, unknown>
}

/** The PostHog identity operations, behind a seam. */
export interface IdentitySink {
  alias(userId: string): void
  identify(userId: string, setOnce?: Record<string, unknown>): void
  reset(): void
  /**
   * ADR 6.9.26m §1b — write properties onto the person this browser already
   * is, with no capture and no re-identify.
   *
   * **Optional deliberately.** 6.9.26j's own recording fake predates this
   * member, and its AC8 asserts against that fake; a required member would
   * make widening this interface an edit to that ADR's test. The cost is that
   * an injected sink which omits it drops the write silently, which is why the
   * one place that matters — 6.9.26m AC3 — records it.
   *
   * **Not `identify`.** 6.9.26m §1b assumed this could ride `identify`'s
   * three-argument posthog-js spelling, as the phones do
   * (`AnalyticsServiceFlashcard.kt:56-60` passes `PostHog.distinctId()` back to
   * it). It cannot: the sink's `identify` takes a userId this module has no way
   * to supply outside the sign-in path — a learner who reloads the page and
   * reviews has been through no `signedIn` call this page load — and carries
   * only `setOnce`. `setPersonProperties` is posthog-js's own answer to exactly
   * that, and it is the *narrower* call: it writes properties and nothing else,
   * where a stray `identify` would emit `$identify` and could re-merge a
   * stream. See the As-built note in 6.9.26m §1b.
   */
  setUserProperties?(properties: UserProperties): void
}

/**
 * The default sink: the same `posthog-js` singleton `telemetry/index.ts`
 * constructed, reached the only way that adds no export to a 6.9.26c file.
 *
 * `attached()` is the consent gate — it reports whether `index.ts` ever
 * attached an instance — and the dynamic `import()` returns the module that
 * `init()` was called on, because `posthog-js`' default export *is* the
 * singleton. Dynamic, so nothing here pulls the SDK into the entry chunk that
 * 6.9.26c §4 puts a ceiling on.
 *
 * **The queue is what preserves the ordering across the await.** `signedIn`
 * calls `alias` then `identify` synchronously, but each operation's own work
 * resumes on a microtask; chaining them through one promise is what stops
 * `identify` overtaking the `alias` it must follow. A rejection is swallowed
 * for `initTelemetry`'s reason — this runs under a `void` at a call site that
 * must not become an unhandled rejection because a chunk failed to load.
 */
let queue: Promise<unknown> = Promise.resolve()

/**
 * The slice of `posthog-js` this module drives — a structural type rather than
 * the SDK's own, for `capture.ts`'s reason: importing the type would become a
 * static dependency the moment someone widened it.
 *
 * Note `identify`'s **third** parameter. The second is `$set` (mutable) and the
 * third is `$set_once` (frozen at its first value), so `registered_at` goes in
 * the third with `undefined` in the second — passing it as ordinary properties
 * would make "first registered" re-writable.
 */
interface PostHogIdentityLike {
  alias(userId: string): void
  identify(
    userId: string,
    set?: Record<string, unknown>,
    setOnce?: Record<string, unknown>,
  ): void
  setPersonProperties(
    set?: Record<string, unknown>,
    setOnce?: Record<string, unknown>,
  ): void
  reset(): void
}

function enqueue(operation: (posthog: PostHogIdentityLike) => void): void {
  if (!attached().analytics) return
  queue = queue
    .then(async () => {
      const { default: posthog } = await import('posthog-js')
      operation(posthog as unknown as PostHogIdentityLike)
    })
    .catch((error) => console.warn('telemetry identity failed', error))
}

const posthogSink: IdentitySink = {
  alias: (userId) => enqueue((posthog) => posthog.alias(userId)),
  identify: (userId, setOnce) =>
    enqueue((posthog) => posthog.identify(userId, undefined, setOnce)),
  reset: () => enqueue((posthog) => posthog.reset()),
  setUserProperties: ({ set, setOnce }) =>
    enqueue((posthog) => posthog.setPersonProperties(set, setOnce)),
}

let sink: IdentitySink = posthogSink

/** Test seam: swap the sink, exactly as `AnalyticsIdentity.sink` is swapped. */
export function setIdentitySink(replacement: IdentitySink): void {
  sink = replacement
}

/** Test seam: put the real posthog-backed sink back. */
export function resetIdentitySink(): void {
  sink = posthogSink
}

/**
 * Weld this browser's event stream to `userId`.
 *
 * **The ordering is load-bearing, and is inherited rather than re-derived**
 * (13.8.26a D1, `AnalyticsIdentity.kt:54-74`):
 *
 * 1. `alias(userId)` **while still anonymous** — the client SDK's one-argument
 *    form links the *current* distinct id to the supplied one.
 * 2. **Then** `identify(userId)`, which is itself the merging call: it emits
 *    `$identify` carrying `$anon_distinct_id`, and that is what actually welds
 *    the pre-signup stream to the account.
 *
 * `alias` fires only on registration — the merge belongs to the one moment the
 * account was created, so a login identifies and never aliases.
 *
 * `registered_at` goes through `setOnce`, matching `AnalyticsServiceAuth.kt:30`.
 * Passing it as ordinary properties would make it `$set` and re-writable, which
 * is the opposite of what "first registered" means.
 */
export function signedIn(userId: string, isRegistration: boolean): void {
  if (isRegistration) {
    sink.alias(userId)
    sink.identify(userId, { registered_at: new Date().toISOString() })
    return
  }
  sink.identify(userId)
}

/**
 * Sign-out — voluntary **or** involuntary, which is why this one lives on the
 * shared `signOutLocally()` rather than on the refusal branches §1's events use.
 * Both must forget the person.
 *
 * **Never call this before `signedIn` has merged**: `reset` regenerates the
 * anonymous id, so a reset that races ahead of the merge destroys the very join
 * it exists to protect. Its absence is a correctness bug rather than a gap, and
 * 13.8.26a's words are *more* true in a browser than on a phone — "without it,
 * two people sharing a device become one person in the data."
 */
export function signedOut(): void {
  sink.reset()
}

/**
 * ADR 6.9.26m §1b — the two activation properties the phones write beside a
 * capture, on the web's one identity rail.
 *
 * It lives here rather than beside the capture for 6.9.26l's reason, which this
 * ADR agrees with: a user-property write is not a capture concern and belongs
 * with the identity join. What it adds to that reasoning is the payoff —
 * `first_review_session_at` is one of the two properties
 * `lifecycle-funnel.toml:80` names as the definition of activation, so a
 * learner whose first real act was reviewing on a desk starts carrying the
 * record that says so.
 *
 * A no-op when PostHog was never constructed, because `enqueue` reads the same
 * `attached()` gate every other operation here does — no call site branches on
 * consent (6.9.26c).
 */
export function setUserProperties(properties: UserProperties): void {
  sink.setUserProperties?.(properties)
}
